import { useMemo } from 'react';
import { map, scan, shareReplay } from 'rxjs';
import { useRecentSymbols } from '../../contexts/RecentSymbolsContext';
import { useObservableValue } from '../../hooks/useObservable';
import { wsScanToMap } from '../../pipes';
import type { Security } from '../../types/Security';
import { supportsBPSPricing } from '../../utils';
import { EMPTY_ARRAY } from '../../utils/empty';
import { isActiveSecurity, shouldDeleteSecurityFromIndex, type SecurityMemoryObservable } from './utils';

export { getFuturesByMarketByCurrencyPair } from './futureIndexes';
export { getExpirationByMarketByCurrencyIdentity, getStrikesByOptionKey } from './optionIndexes';
export { getCurrencyPairFromSecurity, isActiveSecurity } from './utils';

export function useCoreSecurityIndexes(securityMemoryObs: SecurityMemoryObservable) {
  const { recentSymbolsList } = useRecentSymbols();

  const latestSecurityMessageObs = useMemo(
    () => securityMemoryObs.pipe(map(({ latestMessage }) => latestMessage)),
    [securityMemoryObs]
  );
  const securitiesBySymbolObs = useMemo(
    () => securityMemoryObs.pipe(map(({ memory }) => new Map(memory))),
    [securityMemoryObs]
  );
  const activeSecuritiesBySymbolObs = useMemo(
    () =>
      latestSecurityMessageObs.pipe(
        wsScanToMap<Security, string>({
          getUniqueKey: security => security.Symbol,
          newMapEachUpdate: true,
          shouldDelete: shouldDeleteSecurityFromIndex,
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      ),
    [latestSecurityMessageObs]
  );
  const securitiesListObs = useMemo(
    () =>
      latestSecurityMessageObs.pipe(
        wsScanToMap<Security, string>({
          getUniqueKey: security => security.Symbol,
          newMapEachUpdate: false,
          isRelevant: isActiveSecurity,
          shouldDelete: shouldDeleteSecurityFromIndex,
        }),
        map(securitiesBySymbol => [...securitiesBySymbol.values()]),
        shareReplay({ bufferSize: 1, refCount: false })
      ),
    [latestSecurityMessageObs]
  );
  const displaySortedSecuritiesObs = useMemo(
    () =>
      securitiesListObs.pipe(
        map(securities =>
          securities.toSorted((a, b) => (a.DisplaySymbol ?? a.Symbol).localeCompare(b.DisplaySymbol ?? b.Symbol))
        )
      ),
    [securitiesListObs]
  );
  const securitiesListSortedByRankObs = useMemo(
    () =>
      securitiesListObs.pipe(
        map(securities => securities.toSorted((a, b) => ((a.Rank ?? Infinity) < (b.Rank ?? Infinity) ? -1 : 1)))
      ),
    [securitiesListObs]
  );

  const hasBPSPricingSecuritiesObs = useMemo(
    () =>
      latestSecurityMessageObs.pipe(
        // This scan can only go from false -> true, it can never go from true -> false (eg if some security is removed that this was predicated on)
        // This is a simplicity and performance trade-off: we avoid iterating over all securities
        scan(
          (hasBPSPricingSecurities, message) =>
            hasBPSPricingSecurities ||
            message.data.some(security => isActiveSecurity(security) && supportsBPSPricing(security)),
          false
        )
      ),
    [latestSecurityMessageObs]
  );

  const securitiesBySymbolValue = useObservableValue(() => securitiesBySymbolObs, [securitiesBySymbolObs], new Map());
  const hasReceivedSecuritiesMessageObs = useMemo(
    () => latestSecurityMessageObs.pipe(map(() => true)),
    [latestSecurityMessageObs]
  );
  const hasReceivedSecuritiesMessage = useObservableValue(
    () => hasReceivedSecuritiesMessageObs,
    [hasReceivedSecuritiesMessageObs],
    false
  );
  const activeSecuritiesBySymbolValue = useObservableValue(
    () => activeSecuritiesBySymbolObs,
    [activeSecuritiesBySymbolObs],
    new Map()
  );
  const securitiesList = useObservableValue(() => securitiesListObs, [securitiesListObs], EMPTY_ARRAY);
  const displaySortedSecurities = useObservableValue(
    () => displaySortedSecuritiesObs,
    [displaySortedSecuritiesObs],
    EMPTY_ARRAY
  );
  const securitiesListSortedByRank = useObservableValue(
    () => securitiesListSortedByRankObs,
    [securitiesListSortedByRankObs],
    EMPTY_ARRAY
  );
  const hasBPSPricingSecurities = useObservableValue(
    () => hasBPSPricingSecuritiesObs,
    [hasBPSPricingSecuritiesObs],
    false
  );

  // Recents are user-local state, so keep that ordering as a React-level projection
  // over the already display-sorted active securities.
  const searchableSecurities = useMemo(() => {
    if (!hasReceivedSecuritiesMessage) {
      return undefined;
    }

    return buildSearchableSecurities(displaySortedSecurities, activeSecuritiesBySymbolValue, recentSymbolsList);
  }, [activeSecuritiesBySymbolValue, displaySortedSecurities, hasReceivedSecuritiesMessage, recentSymbolsList]);

  return useMemo(
    () => ({
      isLoaded: hasReceivedSecuritiesMessage && searchableSecurities != null,
      hasBPSPricingSecurities,
      securitiesBySymbol: securitiesBySymbolValue,
      securitiesBySymbolObs,
      securitiesList,
      searchableSecurities,
      securitiesListSortedByRank,
    }),
    [
      hasBPSPricingSecurities,
      hasReceivedSecuritiesMessage,
      searchableSecurities,
      securitiesBySymbolValue,
      securitiesBySymbolObs,
      securitiesList,
      securitiesListSortedByRank,
    ]
  );
}

/**
 * Applies local recency ordering on top of the stream-maintained display sort.
 * Recent symbols are capped at five and de-duped from the remainder of the list.
 */
export function buildSearchableSecurities(
  displaySortedSecurities: Security[],
  activeSecuritiesBySymbol: Map<string, Security>,
  recentSymbolsList: string[]
): Security[] {
  const recentSymbols = recentSymbolsList.slice(0, 5).reverse();
  const recentSecurities = recentSymbols.flatMap(symbol => {
    const security = activeSecuritiesBySymbol.get(symbol);
    return security == null ? [] : [security];
  });
  const recentSymbolsSet = new Set(recentSymbols);
  return recentSecurities.concat(displaySortedSecurities.filter(sec => !recentSymbolsSet.has(sec.Symbol)));
}
